import React, { useState, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { State } from '../../state-actions-reducers'
import {
  copyContactPerson,
  copyContactPersonEmailPhone,
  accountantCopyHeadPerson,
  accountantCopyContactPerson,
  accountantCopyContactEmailPhone,
} from '../../utils/helpers'
import { Lang, Country } from '../../types/common-types'

import TextInput from '../../shared/Inputs/TextInput/TextInput'
import PhoneInput from '../../shared/Inputs/PhoneInput/PhoneInput'
import EmailInput from '../../shared/Inputs/EmailInput/EmailInput'
import styles from './FiscalScreenThree.module.scss'
import cn from 'classnames'
import warningImage from '../../images/warning.svg'
import SocialLinksInput from '../../shared/Inputs/SocialLinksInput/SocialLinksInput'
import { smartValidateString, validatePhone } from '../../validate/validators'
import CheckBoxInput from '../../shared/Inputs/CheckBoxInput/CheckBoxInput'
import MessengersInput from '../../shared/Inputs/MessengersInput/MessengersInput'
import MessengersCheckbox from '../../shared/MessengersCheckbox/MessengersCheckbox'
import AnotherRentalsInputs from '../../shared/Inputs/AnotherRentalsInputs/AnotherRentalsInputs'
import InfoMessage from '../../shared/InfoMessage/InfoMessage'

interface Props {
  lang: Lang
  accountForm: any
  allCitizenships: Country[]
}

/* THE COMPONENT */
function FiscalScreenThree({
  lang,
  accountForm,
  allCitizenships,
}: Props) {
  const { watch, setValue, getValues, formState: { errors } } = useFormContext<State>()
  const state = watch()

  return (
    <>
      <main className={styles.main}>
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3>{accountForm.stepTwoContactsTitle[lang]}</h3>
            </div>
            <div className="col-4 col-xs-12">
              {/* F_ContactPhoneNumber */}
              <PhoneInput
                testName="contactPhones"
                testErrorName="error-contactPhones"
                label={accountForm.contactPersonPhone.label[lang]}
                lang={lang}
                numbers={
                  state.contactPhones.length === 0 ? [''] : state.contactPhones
                }
                setNumbers={contactPhones => {
                  if (!state.Confirm_phone_ok) {
                    setValue('contactPhones', contactPhones, { shouldDirty: true })
                  }
                  if (state.copyContactPersonEmailPhone)
                    setValue('contactPersonPhones', contactPhones, { shouldDirty: true })
                }}
                required
                firstlocked={state.Confirm_phone_ok}
                valid={!errors.contactPhones}
                invalidText={accountForm.contactPersonPhone.invalidText[lang]}
              />
            </div>
            <div className="col-4 col-xs-12">
              <PhoneInput
                testName="contactPhoneSeconds"
                testErrorName="error-contactPhoneSeconds"
                label={accountForm.additionalPhoneNumbers.label[lang]}
                lang={lang}
                numbers={
                  state.contactPhoneSeconds.length === 0
                    ? ['']
                    : state.contactPhoneSeconds
                }
                setNumbers={data => {
                  setValue('contactPhoneSeconds', data, { shouldDirty: true })
                  if (!state.Confirm_phone_ok)
                    setValue('Confirm_phone', data[0] || '', { shouldDirty: true })
                }}
                valid={!errors.contactPhoneSeconds}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-4 col-xs-12">
              <EmailInput
                testName={'contactEmails'}
                testErrorName={'error-contactEmails'}
                emails={
                  state.contactEmails.length === 0 ? [''] : state.contactEmails
                }
                setEmails={contactEmail => {
                  setValue('contactEmails', contactEmail, { shouldDirty: true })
                  if (!state.Confirm_mail_ok)
                    setValue('Confirm_mail', contactEmail || '', { shouldDirty: true })
                  if (state.copyContactPersonEmailPhone)
                    setValue('contactPersonEmails', contactEmail, { shouldDirty: true })
                }}
                placeholder={accountForm.email.placeholder[lang]}
                accountForm={accountForm}
                lang={lang}
                required
                invalidText={accountForm.email.invalidText[lang]}
                valid={!errors.contactEmails}
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* PERSONAL WEBSITE */}
              <TextInput
                testName={'personalWebsite'}
                testErrorName={'error-personalWebsite'}
                lang={lang}
                content={accountForm.personalWebsite}
                value={state.personalWebsite[0]}
                onChange={data => (
                  setValue('personalWebsite', [data], { shouldDirty: true }),
                  console.log(state.personalWebsite)
                )}
                valid={!errors.personalWebsite}
              />
            </div>
            <div className="col-8 col-xs-12">
              {/* PURPOSE OF LEASE */}
              <TextInput
                testName={'purposeOfLease'}
                testErrorName={'error-purposeOfLease'}
                disabled={!state.agreed}
                lang={lang}
                content={accountForm.purposeOfLease}
                onChange={purposeOfLease =>
                  setValue('purposeOfLease', purposeOfLease, { shouldDirty: true })
                }
                value={state.purposeOfLease || ''}
                valid={
                  state.purposeOfLease.length > 0
                    ? smartValidateString(state.purposeOfLease) &&
                    !errors.purposeOfLease
                    : !errors.purposeOfLease
                }
                required
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3>{accountForm.stepTwoContactTitle[lang]}</h3>
            </div>
            <div className={styles.checkInputBox + ' ' + 'col-12'}>
              <input
                id="copyInputContactsPerson"
                name="copyInputContactsPerson"
                type="checkBox"
                onChange={e => {
                  const current = getValues()
                  const next = copyContactPerson(current as State)
                  Object.keys(next).forEach(key => {
                    if ((next as any)[key] !== (current as any)[key]) {
                      setValue(key as any, (next as any)[key], { shouldDirty: true })
                    }
                  })
                }}
                checked={state.copyContactPerson}
              />
              <label htmlFor="copyInputContactsPerson">
                {state.F_FiscalType === '1'
                  ? accountForm.fiscalIpContactsCopyBtnDesc[lang]
                  : accountForm.fiscalContactsCopyBtnDesc[lang]}
              </label>
            </div>
            <div className="col-4 col-xs-12">
              {/* CONTACT_LASTNAME */}
              <TextInput
                testName={'contactPersonLastname'}
                testErrorName={'error-contactPersonLastname'}
                disabled={!state.agreed || state.copyContactPerson}
                lang={lang}
                content={accountForm.lastName}
                onChange={contactPersonLastname =>
                  setValue('contactPersonLastname', contactPersonLastname, { shouldDirty: true })
                }
                value={state.contactPersonLastname || ''}
                valid={
                  state.contactPersonLastname.length > 0
                    ? smartValidateString(state.contactPersonLastname) &&
                    !errors.contactPersonLastname
                    : !errors.contactPersonLastname
                }
                required
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* CONTACT_FIRSTNAME */}
              <TextInput
                testName={'contactPersonFirstname'}
                testErrorName={'error-contactPersonFirstname'}
                disabled={!state.agreed || state.copyContactPerson}
                lang={lang}
                content={accountForm.firstName}
                onChange={contactPersonFirstname =>
                  setValue('contactPersonFirstname', contactPersonFirstname, { shouldDirty: true })
                }
                value={state.contactPersonFirstname || ''}
                valid={
                  state.contactPersonFirstname.length > 0
                    ? smartValidateString(state.contactPersonFirstname) &&
                    !errors.contactPersonFirstname
                    : !errors.contactPersonFirstname
                }
                required
              />
            </div>
            <div className="col-4 col-xs-12">
              {/* CONTACT_PATRONYMIC */}
              <TextInput
                testName={'contactPersonPatronymic'}
                testErrorName={'error-contactPersonPatronymic'}
                testCheckName={'contactPersonNoPatronymic'}
                disabled={
                  !state.agreed ||
                  state.noPatronymicContact ||
                  state.copyContactPerson
                }
                lang={lang}
                content={accountForm.patronymic}
                onChange={contactPersonPatronymic =>
                  setValue('contactPersonPatronymic', contactPersonPatronymic, { shouldDirty: true })
                }
                checkLabel={accountForm.noPatronymic[lang]}
                checkValue={
                  state.noPatronymicContact ||
                  (state.copyContactPerson && state.noPatronymic)
                }
                checkChange={() => {
                  if (!state.copyContactPerson) {
                    setValue('noPatronymicContact', !state.noPatronymicContact, { shouldDirty: true })
                    setValue('contactPersonPatronymic', '', { shouldDirty: true })
                  }
                }}
                value={state.contactPersonPatronymic || ''}
                valid={
                  state.contactPersonPatronymic.length > 0
                    ? smartValidateString(state.contactPersonPatronymic) &&
                    !errors.contactPersonPatronymic
                    : !errors.contactPersonPatronymic
                }
                required
              />
            </div>
          </div>
          <div className='row'>
            <div className="col-4 col-xs-12">
              {/* CONTACT_POSITION */}
              <TextInput
                testName={'contactPersonPosition'}
                testErrorName={'error-contactPersonPosition'}
                disabled={!state.agreed || state.copyContactPerson && state.F_FiscalType !== '1'}
                lang={lang}
                content={accountForm.F_Contact}
                onChange={contactPersonPosition =>
                  setValue('contactPersonPosition', contactPersonPosition, { shouldDirty: true })
                }
                value={state.contactPersonPosition || ''}
                valid={
                  state.contactPersonPosition.length > 0
                    ? smartValidateString(state.contactPersonPosition) &&
                    !errors.contactPersonPosition
                    : !errors.contactPersonPosition
                }
                required
              />
            </div>
          </div>
          <div className='row'>
            <div className="col-12 col-xs-12">
              <div className={styles.checkInputBox}>
                <input
                  id="copyInputContacts"
                  name="copyInputContacts"
                  type="checkBox"
                  onChange={e => {
                    const current = getValues()
                    const next = copyContactPersonEmailPhone(current as State)
                    Object.keys(next).forEach(key => {
                      if ((next as any)[key] !== (current as any)[key]) {
                        setValue(key as any, (next as any)[key], { shouldDirty: true })
                      }
                    })
                  }}
                  checked={state.copyContactPersonEmailPhone}
                />
                <label htmlFor="copyInputContacts">
                  {accountForm.fiscalContactsPersonCopyBtnDesc[lang]}
                </label>
              </div>
            </div>
            <div className="col-4 col-xs-12">
              <PhoneInput
                testName="contactPersonPhone"
                testErrorName="error-contactPersonPhone"
                label={accountForm.contactPersonPhone.label[lang]}
                lang={lang}
                numbers={
                  state.contactPersonPhones.length === 0
                    ? ['']
                    : state.contactPersonPhones
                }
                setNumbers={data =>
                  setValue('contactPersonPhones', data, { shouldDirty: true })
                }
                invalidText={accountForm.contactPersonPhone.invalidText[lang]}
                deleteText={accountForm.removeFieldButton[lang]}
                addField={accountForm.addFieldButton[lang]}
                valid={!errors.contactPersonPhones}
                required
                disabled={state.copyContactPersonEmailPhone}
              />
              <MessengersCheckbox lang={lang} accountForm={accountForm} state={state}  phoneNumber={state.contactPersonPhones[0]} />
            </div>
            {/* CONTACT_EMAIL */}
            <div className="col-4 col-xs-12">
              <EmailInput
                testName={'contactPersonEmails'}
                testErrorName={'error-contactPersonEmails'}
                emails={
                  state.contactPersonEmails.length === 0
                    ? ['']
                    : state.contactPersonEmails
                }
                setEmails={contactPersonEmails => {
                  setValue('contactPersonEmails', contactPersonEmails, { shouldDirty: true })
                  if (!state.Confirm_mail_ok)
                    setValue('Confirm_mail', contactPersonEmails[0] || '', { shouldDirty: true })
                }}
                placeholder={accountForm.email.placeholder[lang]}
                invalidText={accountForm.email.invalidText[lang]}
                accountForm={accountForm}
                lang={lang}
                required
                valid={!errors.contactPersonEmails}
                disabled={state.copyContactPersonEmailPhone}
              />
            </div>
          </div>
        </div>
        <MessengersInput
          lang={lang}
          
          allCitizenships={allCitizenships}
          phoneNumbers={state.contactPersonPhones}
        />
        <SocialLinksInput
          lang={lang}
          
          accountForm={accountForm}
        />
        {state.F_FiscalType !== '1' &&
          <div className="form-group">
            <div className="row">
              <div className="col-12 col-xs-12">
                <h3>{accountForm.stepTwoAccountantPersonTitle[lang]}</h3>
              </div>
              <div className={cn(styles.checkInputCol, 'col-12')}>
                <div className={styles.checkInputBox}>
                  <input
                    id="copyInputAccountantsHeadPerson"
                    name="copyInputAccountantsHeadPerson"
                    type="checkBox"
                    onChange={e => {
                      const current = getValues()
                      const next = accountantCopyHeadPerson(current as State)
                      Object.keys(next).forEach(key => {
                        if ((next as any)[key] !== (current as any)[key]) {
                          setValue(key as any, (next as any)[key], { shouldDirty: true })
                        }
                      })
                    }}
                    checked={state.accountantCopyHeadPerson}
                  />
                  <label htmlFor="copyInputAccountantsHeadPerson">
                    {accountForm.fiscalAccountantsCopyHeadBtnDesc[lang]}
                  </label>
                </div>
                <div className={styles.checkInputBox}>
                  <input
                    id="copyInputAccountantsPerson"
                    name="copyInputAccountantsPerson"
                    type="checkBox"
                    onChange={e => {
                      const current = getValues()
                      const next = accountantCopyContactPerson(current as State)
                      Object.keys(next).forEach(key => {
                        if ((next as any)[key] !== (current as any)[key]) {
                          setValue(key as any, (next as any)[key], { shouldDirty: true })
                        }
                      })
                    }}
                    checked={state.accountantCopyContactPerson}
                  />
                  <label htmlFor="copyInputAccountantsPerson">
                    {accountForm.fiscalAccountantsPersonCopyBtnDesc[lang]}
                  </label>
                </div>
              </div>
              <div className="col-4 col-xs-12">
                {/* ACCOUNTANT_LASTNAME */}
                <TextInput
                  testName={'accountantPersonLastname'}
                  testErrorName={'error-accountantPersonLastname'}
                  disabled={!state.agreed || state.accountantCopyContactPerson || state.accountantCopyHeadPerson}
                  lang={lang}
                  content={accountForm.lastName}
                  onChange={accountantPersonLastname =>
                    setValue('accountantPersonLastname', accountantPersonLastname, { shouldDirty: true })
                  }
                  value={state.accountantPersonLastname || ''}
                  valid={
                    state.accountantPersonLastname.length > 0
                      ? smartValidateString(state.accountantPersonLastname) &&
                      !errors.accountantPersonLastname
                      : !errors.accountantPersonLastname
                  }
                  required
                />
              </div>
              <div className="col-4 col-xs-12">
                {/* ACCOUNTANT_FIRSTNAME */}
                <TextInput
                  testName={'accountantPersonFirstname'}
                  testErrorName={'error-accountantPersonFirstname'}
                  disabled={!state.agreed || state.accountantCopyContactPerson || state.accountantCopyHeadPerson}
                  lang={lang}
                  content={accountForm.firstName}
                  onChange={accountantPersonFirstname =>
                    setValue('accountantPersonFirstname', accountantPersonFirstname, { shouldDirty: true })
                  }
                  value={state.accountantPersonFirstname || ''}
                  valid={
                    state.accountantPersonFirstname.length > 0
                      ? smartValidateString(state.accountantPersonFirstname) &&
                      !errors.accountantPersonFirstname
                      : !errors.accountantPersonFirstname
                  }
                  required
                />
              </div>
              <div className="col-4 col-xs-12">
                {/* ACCOUNTANT_PATRONYMIC */}
                <TextInput
                  testName={'accountantPersonPatronymic'}
                  testErrorName={'error-accountantPersonPatronymic'}
                  testCheckName={'accountantPersonNoPatronymic'}
                  disabled={
                    !state.agreed ||
                    state.accountantCopyContactPerson || state.accountantCopyHeadPerson
                  }
                  lang={lang}
                  content={accountForm.patronymic}
                  onChange={accountantPersonPatronymic =>
                    setValue('accountantPersonPatronymic', accountantPersonPatronymic, { shouldDirty: true })
                  }
                  checkLabel={accountForm.noPatronymic[lang]}
                  checkChange={() => {
                    if (!state.accountantCopyContactPerson) {
                      setValue('accountantPersonPatronymic', '', { shouldDirty: true })
                    }
                  }}
                  value={state.accountantPersonPatronymic || ''}
                  valid={
                    state.accountantPersonPatronymic.length > 0
                      ? smartValidateString(state.accountantPersonPatronymic) &&
                      !errors.accountantPersonPatronymic
                      : !errors.accountantPersonPatronymic
                  }
                  required
                />
              </div>
            </div>
            <div className='row'>
              <div className="col-12 col-xs-12">
                <div className={styles.checkInputBox}>
                  <input
                    id="copyInputAccountants"
                    name="copyInputAccountants"
                    type="checkBox"
                    onChange={e => {
                      const current = getValues()
                      const next = accountantCopyContactEmailPhone(current as State)
                      Object.keys(next).forEach(key => {
                        if ((next as any)[key] !== (current as any)[key]) {
                          setValue(key as any, (next as any)[key], { shouldDirty: true })
                        }
                      })
                    }}
                    checked={state.accountantCopyContactEmailPhone}
                  />
                  <label htmlFor="copyInputAccountants">
                    {accountForm.fiscalAccountantPersonEmailPhone[lang]}
                  </label>
                </div>
              </div>
              <div className="col-4 col-xs-12">
                <PhoneInput
                  testName="accountantPersonPhone"
                  testErrorName="error-accountantPersonPhone"
                  label={accountForm.mobilePhoneNumber.label[lang]}
                  lang={lang}
                  numbers={
                    state.accountantPersonPhones.length === 0
                      ? ['']
                      : state.accountantPersonPhones
                  }
                  setNumbers={data =>
                    setValue('accountantPersonPhones', data, { shouldDirty: true })
                  }
                  invalidText={accountForm.mobilePhoneNumber.invalidText[lang]}
                  deleteText={accountForm.removeFieldButton[lang]}
                  addField={accountForm.addFieldButton[lang]}
                  valid={!errors.accountantPersonPhones}
                  required
                  disabled={state.accountantCopyContactEmailPhone}
                />
              </div>
              {/* ACCOUNTANT_EMAIL */}
              <div className="col-4 col-xs-12">
                <EmailInput
                  testName={'accountantPersonEmails'}
                  testErrorName={'error-accountantPersonEmails'}
                  emails={
                    state.accountantPersonEmails.length === 0
                      ? ['']
                      : state.accountantPersonEmails
                  }
                  setEmails={accountantPersonEmails => {
                    setValue('accountantPersonEmails', accountantPersonEmails, { shouldDirty: true })
                    if (!state.Confirm_mail_ok)
                      setValue('Confirm_mail', accountantPersonEmails[0] || '', { shouldDirty: true })
                  }}
                  placeholder={accountForm.email.placeholder[lang]}
                  invalidText={accountForm.email.invalidText[lang]}
                  accountForm={accountForm}
                  lang={lang}
                  required
                  valid={!errors.accountantPersonEmails}
                  disabled={state.accountantCopyContactEmailPhone}
                />
              </div>
              <div className="col-4 col-xs-12">
                {/* ACCOUNTANT_POSITION */}
                <TextInput
                  testName={'accountantPersonPosition'}
                  testErrorName={'error-accountantPersonPosition'}
                  lang={lang}
                  content={accountForm.F_Contact}
                  onChange={accountantPersonPosition =>
                    setValue('accountantPersonPosition', accountantPersonPosition, { shouldDirty: true })
                  }
                  value={state.accountantPersonPosition || ''}
                  valid={
                    state.accountantPersonPosition.length > 0
                      ? smartValidateString(state.accountantPersonPosition) &&
                      !errors.accountantPersonPosition
                      : !errors.accountantPersonPosition
                  }
                  disabled={
                    !state.agreed ||
                    state.accountantCopyContactPerson || state.accountantCopyHeadPerson
                  }
                  required
                />
              </div>
            </div>
          </div>
        }
        <AnotherRentalsInputs 
          lang={lang}
          
          accountForm={accountForm}
        />
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-xs-12">
              <h3>{accountForm.stepTwoRecomendation[lang]}</h3>
            </div>
            <div className="col-8 col-xs-12">
              <TextInput
                testName={'recommendations'}
                testErrorName={'error-recommendations'}
                lang={lang}
                content={accountForm.recommendations}
                value={state.recommendations}
                onChange={data =>
                  setValue('recommendations', data, { shouldDirty: true })
                }
                tooltip={accountForm.recommendationsTooltip[lang]}
                valid={!errors.recommendations}
                required
              />
            </div>
            <div className="col-12 col-xs-12">
              <p className={styles.warningMessage}>
                <img src={warningImage} alt="warningImage"></img>
                {accountForm.stepRecomendationWarning[lang]}
              </p>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

export default FiscalScreenThree
